import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl/mapbox';

import type { FC } from 'react';

import ResetIcon from '@/assets/icons/reset.svg?react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import usePinnedItem from '@/hooks/use-pinned-item';
import { MAPBOX_INITIAL_VIEW_STATE, MAX_ZOOM_IN, STATION_SOURCE } from '@/utils/constants/map';

interface ResetButtonProps {
  initialZoom?: number | null;
}

const ResetButton: FC<ResetButtonProps> = ({ initialZoom }) => {
  const { t } = useTranslation();
  const { current: map } = useMap();
  const { pinnedItemId, pinnedItemType } = usePinnedItem();
  const isTrainPinned = pinnedItemId && pinnedItemType === 'route';
  const isStationPinned = pinnedItemId && pinnedItemType === 'station';
  const isInteractive = !isTrainPinned;

  const resetMapView = () => {
    if (!map) {
      return;
    }

    if (isStationPinned && pinnedItemId) {
      const features = map.querySourceFeatures(STATION_SOURCE, {
        filter: ['==', ['get', 'id'], pinnedItemId],
      });

      const stationFeature = features[0];

      if (stationFeature?.geometry.type === 'Point') {
        map.flyTo({
          center: stationFeature.geometry.coordinates as [number, number],
          zoom: MAX_ZOOM_IN,
          duration: 1000,
          essential: true,
        });

        return;
      }
    }

    map?.flyTo({
      center: [MAPBOX_INITIAL_VIEW_STATE.longitude, MAPBOX_INITIAL_VIEW_STATE.latitude],
      zoom: initialZoom || MAPBOX_INITIAL_VIEW_STATE.zoom,
      pitch: 0,
      bearing: 0,
      duration: 1000,
      essential: true,
    });
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Button
            className="bg-white size-10 flex justify-center items-center rounded-full shadow-sm p-0"
            onClick={resetMapView}
            aria-label={t('common.reset_position')}
            disabled={!isInteractive}
          >
            <ResetIcon className="size-6" />
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p>{t('common.reset_position')}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ResetButton;
